import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit, Renderer2, HostListener } from '@angular/core';
import { BaseComponent } from './moov-common/base/base.component';
import { AccountTypeEnum, Auth0OrighinEnum } from './moov-common/services/personal-user-data/personal-user-data';
import { PersonalUserDataService } from './moov-common/services/personal-user-data/personal-user-data.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NAVIGATION_ROUTES } from './navigation-routes.const';
import { NavigationEnd, Router } from '@angular/router';
import { LogoutMessageComponent } from './auth/components/logout-message/logout-message.component';
import { LocalStorageKeys } from './moov-common/services/localStorage/local-storage.enum';
import { AbstractLocalStorageService } from './moov-common/services/localStorage/local-storage.abstract.service';
import { AbstractMotivationsService } from './moov-common/services/motivation/motivations.abstract.service';
import { InternalEventsManagerService } from './moov-common/services/internal-events-manager/internal-events-manager.service'
import { MatDialog } from '@angular/material/dialog';
import { Locale } from './moov-common/enum/locale.enum';
import { EnvConfigService } from './moov-common/services/env-config/env-config.service';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { EnvConfigKeys } from './moov-common/services/env-config/env-keys';
import { ResponsesAbstractService } from './moov-common/services/responses/responses.abstract.service';
import { ConfirmationDialogComponent, ConfirmationDialogData } from './moov-common/components/confirmation-dialog/confirmation-dialog.component';
import { ClaroEvents } from './moov-common/services/google-analytics/analytics.enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends BaseComponent implements OnInit {
  isSidebarOpened = false;
  private config: { version: string };
  accountTypeEnum = AccountTypeEnum;
  appPageLocale: string = '';
  shouldPresentAdminPage: boolean = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private personalUserDataService: PersonalUserDataService,
    private router: Router,
    public dialog: MatDialog,
    private localStorageService: AbstractLocalStorageService,
    private motivationsService: AbstractMotivationsService, // This is to allow the constructor of the motivations service to call init map, Ugly - I know.
    private renderer: Renderer2,
    private http: HttpClient,
    private internalEventsService: InternalEventsManagerService,
    private envConfig : EnvConfigService,
    private msalService: MsalService,
    private responsesService: ResponsesAbstractService,
    private translateService: TranslateService
  ) {
    super();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        gtag('config', 'G-P2LFQ4JDN9', {
          page_path: event.urlAfterRedirects,
        });
      }
    });

    this.personalUserDataService = personalUserDataService;
    personalUserDataService.personalilze();
    this.setRTL();
  }

  toggleMenu(opened: boolean) {
    this.isSidebarOpened = opened;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.verifyLatestVersion();
    this.envConfig.loadConfig();
    this.verifyUserOptions();
    this.handleRedirectMSALObservable();
    this.internalEventsService.reloadUserData.subscribe((event) => {
      this.motivationsService.reloadMotivationsMetaData();
      this.verifyUserOptions();
    });

    this.internalEventsService.userLoggedOut.subscribe((event) => {
      this.logoutHandler();
    });

    this.internalEventsService.userLoggedIn.subscribe(() => {
      this.checkPendingPulseCheck();
    });

    this.checkPendingPulseCheck();

    window.addEventListener('storage', this.handleStorageChange.bind(this));
  }

  verifyLatestVersion(): void {
    // get config.json and compare it to the browser version, initate update if different.
    this.config = require('./../assets/common/config.json');
    console.log(`current version: ${this.config.version}`);

    const headers = new HttpHeaders()
      .set('Cache-Control', 'no-cache')
      .set('Pragma', 'no-cache');

    this.http
      .get<{ version: string }>('/assets/common/config.json', { headers })
      .subscribe((config) => {
        if (config.version !== this.config.version) {
          this.http
            .get('', { headers, responseType: 'text' })
            .subscribe(() => location.reload());
        }
      });
  }

  setLocaleForApp() {
    const currentLocale = this.getLocale();

    if (currentLocale == Locale.he_IL) {
      this.appPageLocale = 'he';
    } else {
      this.appPageLocale = 'en';
    }
  }

  verifyUserOptions(): void {
    this.shouldPresentAdminPage = this.isAdminRole;
  }

  logoutHandler() {
    let fullName = this.user?.firstName + ' ' + this.user?.lastName;
    this.localStorageService.clear(LocalStorageKeys.moovToken);
    this.localStorageService.clear(LocalStorageKeys.returnUrl);
    this.localStorageService.clear(LocalStorageKeys.userPersonalInfo);
    this.localStorageService.clear(LocalStorageKeys.resellerSelectedOrg);
    this.localStorageService.clear(LocalStorageKeys.teamViewFilterBYManager);
    this.analytics.logout();
    const dialogRef = this.dialog.open(LogoutMessageComponent, {
      height: '30%',
      width: '30%',
      data: fullName,
    });
    dialogRef.afterClosed().subscribe((result) => {
      this.router.navigateByUrl(NAVIGATION_ROUTES.home);
    });
  }

  logout() {
    this.personalUserDataService.logout();
  }

  setRTL() {
    this.isRTL
      ? (document.body.style.direction = 'rtl')
      : (document.body.style.direction = 'ltr');
  }

  handleStorageChange(event: StorageEvent) {
    // we check if the change is in the userPersonalInfo key or the moovToken key
    // if it is we check if the key is not null and if it has an id
    // if it doesnt have an id or is null we reload the page
    if (
      (event.key === LocalStorageKeys.userPersonalInfo && event.newValue && JSON.parse(event.newValue).id)
      ||
      (event.key === LocalStorageKeys.moovToken && event.newValue === null)
    ) {
      location.reload();
    }
  }

  private checkPendingPulseCheck(): void {
    if (!this.user) return;

    const notificationKey = `pulse_notification_shown_${this.user.id}`;
    if (localStorage.getItem(notificationKey)) return;

    this.responsesService.getPendingPulseCheck().subscribe((data) => {
      if (!data.hasPendingPulseCheck || !data.token) return;

      localStorage.setItem(notificationKey, 'true');

      const genderKey = this.isMale ? 'MALE' : 'FEMALE';
      const message = this.translateService.instant(
        `pulseCheckView.pendingNotification.message.${genderKey}`,
        { name: this.user?.firstName }
      );
      const title = this.translateService.instant('pulseCheckView.pendingNotification.title');
      const goButton = this.translateService.instant('pulseCheckView.pendingNotification.goButton');
      const dismissButton = this.translateService.instant('pulseCheckView.pendingNotification.dismissButton');

      this.analytics.sendClaroEvent(ClaroEvents.PulseCheck_pendingNotificationShown);

      //const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      //  data: {
      //    title: title,
      //    message: message,
      //    confirmButtonText: goButton,
      //    cancelButtonText: dismissButton,
      //  } as ConfirmationDialogData,
      //  height: 'fit-content',
      //  width: this.isMobileView ? '90%' : '30%',
      //  direction: this.isRTL ? 'rtl' : 'ltr',
      //});

      //dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      //  if (confirmed) {
      //    this.router.navigate(['/pulse'], { queryParams: { token: data.token } });
      //  }
      //});
    });
  }

  handleRedirectMSALObservable() {
    this.msalService.handleRedirectObservable()
      .subscribe({
        next: (result: AuthenticationResult | null) => {
          if (result) {
            // These are your tokens:
            const accessToken = result.accessToken;   // for calling APIs
            const idToken     = result.idToken;       // contains user claims
            const state       = result.state;
            const upn         = result.account?.username


                const account = result.account!;
            // result.accessToken here is only for OIDC/Graph, not your API

            // 1) Prepare a request for your custom API scope
            // An access token is issued for a single resource only - requesting a Graph scope
            // alongside the custom API scope makes the request invalid. Graph access is obtained
            // downstream by the backend, which exchanges this token via the on-behalf-of flow.
            const apiTokenRequest = {
              scopes: [
                `api://${this.envConfig.get(EnvConfigKeys.auth0ClientId)}/OBO`
              ],
              account
            };

             // 2) Try to get it silently
            this.msalService.acquireTokenSilent(apiTokenRequest)
            .subscribe({
              next: (tokenRes: AuthenticationResult) => {
                const apiToken = tokenRes.accessToken;
                // console.log(`handleRedirectMSALObservable - NEW SILENT accessToken\n: ${apiToken}`);
                // ← this now has aud=api://547b… so send it to your backend

                this.personalUserDataService.updateUserAuth0Details(upn, Auth0OrighinEnum.MICROSOFT, apiToken, idToken).subscribe((res) => {
                  // console.log(`handleRedirectMSALObservable - res: ${JSON.stringify(res)}`);
                  // Finally, navigate away from the callback route:
                  // console.log(`handleRedirectMSALObservable - upn: ${JSON.stringify(upn)}`);
                  // console.log(`handleRedirectMSALObservable - accessToken: ${accessToken}, idToken: ${idToken}, state: ${state}`);
                  // console.log(`handleRedirectMSALObservable - result: ${JSON.stringify(result)}`);

                  this.router.navigate([NAVIGATION_ROUTES.app.userProfileView]);
                  });

              },
              error: err => {
                // TBD
                // fallback to interactive if silent fails
                console.warn('Silent token failed, doing redirect…', err);
                this.msalService.acquireTokenRedirect(apiTokenRequest);
              }
            });
          }
        },
        error: (err) => {
          console.error('MSAL redirect error', err);
        }
      });
  }
}


declare let gtag: Function;
